import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { useLayoutContext } from '@ott/layout-context';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import { PaymentNames } from '@ott/solar-footer/src/types';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';
import sendKM from '@ott/utility-km';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';

import SupportChat from 'src/components/blocks/SupportChat';
import PartnersSlider from 'src/components/pages/PartnersPage/blocks/PartnersSlider';
import { PAGE_ANCHOR_KEY } from 'src/components/pages/PartnersPage/constants';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useInView from 'src/hooks/useIntersection';
import useIsWebview from 'src/hooks/useIsWebview';
import useToast from 'src/hooks/useToast';
import useTypedSelector from 'src/hooks/useTypedSelector';

import CompletedProjects from './blocks/CompletedProjects';
import FormBlock from './blocks/FormBlock';
import KeyFeatures from './blocks/KeyFeatures';
import MainScreen from './blocks/MainScreen';
import Results from './blocks/Results';
import StartTheJourney from './blocks/StartTheJourney';
import TravelLoyalty from './blocks/TravelLoyalty';
import Toast from './components/Toast';

import styles from './PartnersPage.scss';

const PartnersPage = () => {
  const isWebview = useIsWebview();
  const { isDesktop } = useLayoutContext();
  const history = useHistory();
  const mainScreenRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const travelLoyaltyRef = useRef<HTMLDivElement | null>(null);
  const mainScreenInViewport = useInView({ element: mainScreenRef, repeat: true });
  const formInViewport = useInView({ element: formRef, repeat: true });
  const auth = useTypedSelector(getAuthSelector);
  const dispatch = useDispatch();
  const PaymentMethods = new Set<PaymentNames>([
    PaymentNames.Visa,
    PaymentNames.MasterCard,
    PaymentNames.CryptoCom,
    PaymentNames.Metamask,
    PaymentNames.WalletConnect,
    PaymentNames.Iata,
    PaymentNames.Pci,
    PaymentNames.Aws,
  ]);
  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);
  const isAuthorized = auth?.data?.auth;

  useEffect(() => {
    sendKM('SOLAR_PARTNERS_PAGE_SHOW');

    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  useToast({
    showToast: !mainScreenInViewport && !formInViewport,
    toast: <Toast targetRef={formRef} />,
    anchor: PAGE_ANCHOR_KEY,
    componentClassName: styles.toast,
    containerClassName: styles.messageContainer,
  });

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <>
      {!isWebview && (
        <>
          <CookiePolicy
            cookieLink={COOKIE_POLICY_LINK}
            placement={isDesktop ? 'bottomRight' : 'bottom'}
            isCompact={true}
          />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}
      <main data-locator="partnersPage">
        <MainScreen formRef={formRef} travelLoyaltyRef={travelLoyaltyRef} ref={mainScreenRef} />
        <Container className={styles.container}>
          <TravelLoyalty ref={travelLoyaltyRef} />
          <Results />
        </Container>
        <StartTheJourney />
        <Container className={styles.container}>
          <KeyFeatures />
          <CompletedProjects />
        </Container>
        <PartnersSlider />
        <Container className={styles.container}>
          <FormBlock ref={formRef} data-locator="partnersFormBlock" />
        </Container>
      </main>
      {!isWebview && <Footer paymentMethods={PaymentMethods} />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </>
  );
};

export default PartnersPage;
